import { CdkListboxModule } from '@angular/cdk/listbox';
import { Component, computed, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { getUniqueId } from '../../../functions/get-unique-id';
import {
  ReportField,
  SortBreakOption,
} from '../../../models/api/reports/report';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { SelectComponent } from '../../core/controls/select.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { ReportFieldDialogComponent } from './report-field-dialog.component';
import {
  ReorderableFieldListComponent,
  ReorderableFormChangedEvent,
  ReorderableFormRowDescriptor,
} from './reorderable-field-list.component';

const SORT_ASC = 'ASC';
const SORT_DESC = 'DESC';

type SortFormGroup = FormGroup<{
  id: FormControl<string>;
  breakId: FormControl<number>;
  level: FormControl<number>;
  field: FormControl<ReportField | null>;
  pageBreak: FormControl<boolean>;
  order: FormControl<string>;
  canDelete: FormControl<boolean>;
  updateable: FormControl<boolean>;
}>;

@Component({
  selector: 'app-sort-options',
  imports: [
    PageHeaderComponent,
    SelectComponent,
    ActionRowComponent,
    ButtonComponent,
    CheckboxComponent,
    ReportFieldDialogComponent,
    ReorderableFieldListComponent,
    CdkListboxModule,
  ],
  template: `
    <app-page-header label="Sort fields" headerSize="h3" />

    <fieldset class="tw-border-0 tw-p-0 tw-m-0">
      <legend class="tw-sr-only">Sort field options</legend>
      @if (showAddSort()) {
        <app-action-row>
          <app-button
            left1
            emphasis="Low"
            label="Add sort"
            [disabled]="!canAddMore()"
            (click)="addSort()"
          />
        </app-action-row>
      }
      <div class="tw-pb-6"></div>

      <app-reorderable-field-list
        layout="row"
        focusHost="app-sort-options"
        [formArray]="fg.controls.sorts"
        [mapRow]="mapSortRow"
        [reorderLocked]="sspbLockOrder()"
        [showClearOnTile]="true"
        [rowHighlight]="true"
        removeAriaLabel="Remove sort field"
        [rowExtras]="extras"
        (edit)="onEdit($event.index)"
        (clear)="clearSort($event.index)"
        (changed)="onListChanged($event)"
      />

      <ng-template #extras let-fg let-i="index">
        <div
          class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-gap-2"
        >
          <div class="tw-w-[150px]">
            <app-select
              [control]="fg.controls.order"
              optionLabel="name"
              optionValue="code"
              [options]="sortOptions"
              [filter]="false"
              [showClear]="false"
              [virtualScroll]="false"
              label="Sort Order"
              [showHelpText]="false"
            ></app-select>
          </div>

          @if (getSortConfig(i)?.showPageBreak) {
            <div>
              <app-checkbox
                [control]="fg.controls.pageBreak"
                label="Page break"
                [showHelpText]="false"
              />
            </div>
          }
        </div>
      </ng-template>

      @if (showAddSort()) {
        <div class="tw-italic">Select up to {{ maxSorts() }} sort fields.</div>
      }
    </fieldset>
    <app-report-field-dialog #fieldDialog (selected)="selected($event)" />
  `,
  styles: ``,
})
export class SortOptionsComponent extends BaseComponent implements OnInit {
  @ViewChild('fieldDialog') fieldDialog!: ReportFieldDialogComponent;

  fg = new FormGroup({
    sorts: new FormArray([]),
  });

  current: FormControl<ReportField | null>;

  sortsCount = signal(0);

  /** Bound into the form list host (stable reference). */
  readonly mapSortRow = (
    sortFg: FormGroup,
    i: number,
    _n: number,
  ): ReorderableFormRowDescriptor => {
    const typed = sortFg as SortFormGroup;
    const field = typed.controls.field.value;
    const hasField = !!field;
    return {
      id: typed.controls.id.value,
      label: field?.defaultValue || (hasField ? 'sort field' : 'Select a field'),
      hasField,
      disabled: typed.controls.field.disabled,
      updateable: true,
      showRemove:
        this.isLastFilledSort(i) && typed.controls.canDelete.value,
      removeAriaLabel: 'Remove sort field',
      fixedWidth: true,
      wrapLabel: true,
    };
  };

  maxSorts = computed(() => {
    const report = this.reports.currentReport();
    return report.reportRequest?.maxSortBreakSlots ?? 10;
  });
  canAddMore = computed(() => this.sortsCount() < this.maxSorts());

  showAddSort = computed(() => {
    const max = this.maxSorts();
    if (max === 0) {
      return false;
    }

    const currentCount = this.sortsCount();
    if (currentCount >= max) {
      return false;
    }

    // Match ERF break-level metadata: hide when no remaining template slot can be re-added.
    for (let i = currentCount; i < max; i++) {
      const slot = this.masterSortBreakOptions[i];
      if (!slot || slot.updateable !== false) {
        return true;
      }
    }

    return false;
  });

  sspbLockOrder = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.sspbLockOrder ?? false;
  });

  lockSortOptions = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.lockSortOptions ?? false;
  });

  isReserveReport = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportTemplate?.id === 'SRS_ADHOC_RESERVE';
  });

  private reports = inject(ReportsService);
  private snackbar = inject(Snackbar);
  private masterSortBreakOptions: SortBreakOption[] = [];

  sortOptions = [
    { name: 'Ascending', code: SORT_ASC },
    { name: 'Descending', code: SORT_DESC },
  ];

  get invalid(): boolean {
    return this.fg.invalid;
  }

  get sorts(): SortFormGroup[] {
    return this.fg.controls.sorts.controls as SortFormGroup[];
  }

  ngOnInit(): void {
    this.loadSortBreakOptions();
  }

  /** Reload sort options from the current report. Called by parent on client change. */
  reload(): void {
    this.masterSortBreakOptions = [];
    this.loadSortBreakOptions();
  }

  private loadSortBreakOptions(): void {
    // Clear existing sorts form array before rebuilding
    this.fg.controls.sorts.clear();

    const report = this.reports.currentReport();

    if (report.reportRequest?.sortBreakOptions?.length > 0) {
      this.masterSortBreakOptions = [
        ...report.reportRequest.sortBreakOptions,
      ];

      if (this.isReserveReport()) {
        this.restoreAllOptions(report.reportRequest.sortBreakOptions);
      } else {
        this.restoreFilledOptions(report.reportRequest.sortBreakOptions);
        this.addPlaceholderIfRoom();
      }
      this.sortsCount.set(this.sorts.length);
    }
  }

  private restoreAllOptions(sortBreakOptions: SortBreakOption[]): void {
    sortBreakOptions.forEach((sortBreakOption) => {
      const formGroup = this.createFormGroup(sortBreakOption, true);
      this.fg.controls.sorts.push(formGroup);
    });
  }

  private restoreFilledOptions(sortBreakOptions: SortBreakOption[]): void {
    sortBreakOptions
      .filter((sbo) => sbo.columnName)
      .forEach((sortBreakOption) => {
        const formGroup = this.createFormGroup(sortBreakOption, true);
        this.fg.controls.sorts.push(formGroup);
      });
  }

  private addPlaceholderIfRoom(): void {
    const currentCount = this.sorts.length;

    if (
      currentCount < this.masterSortBreakOptions.length &&
      currentCount < this.maxSorts()
    ) {
      const nextSlot = this.masterSortBreakOptions[currentCount];
      if (nextSlot && nextSlot.updateable !== false) {
        const formGroup = this.createFormGroup(nextSlot, false);
        this.fg.controls.sorts.push(formGroup);
      }
    }
  }

  private createFormGroup(
    sortBreakOption: SortBreakOption,
    withField: boolean,
  ): SortFormGroup {
    const sortOrder = sortBreakOption.defaultSort ?? SORT_ASC;
    const pageBreak = sortBreakOption.pageBreakChecked ?? false;
    const isUpdateable = sortBreakOption.updateable ?? true;

    let field: ReportField | null = null;
    let fieldValidators: (typeof Validators.required)[] = [];

    if (withField && sortBreakOption.columnName) {
      field = {
        columnName: sortBreakOption.columnName,
        defaultValue: sortBreakOption.defaultValue,
        tableAlias: sortBreakOption.tableAlias,
        columnId: 0,
        customSection: null,
        displayOrder: 0,
        inlineFilterType: 'NONE',
        pageBreakChecked: false,
        canDelete: sortBreakOption.canDelete,
        updateable: sortBreakOption.updateable,
      };
      fieldValidators = [Validators.required];
    }

    const formGroup = new FormGroup({
      id: new FormControl(getUniqueId('field')),
      breakId: new FormControl(sortBreakOption.breakId),
      level: new FormControl(sortBreakOption.level),
      field: new FormControl<ReportField | null>(field, fieldValidators),
      pageBreak: new FormControl(pageBreak, []),
      order: new FormControl(sortOrder, []),
      canDelete: new FormControl(sortBreakOption.canDelete ?? true),
      updateable: new FormControl(isUpdateable),
    });

    if (!isUpdateable) {
      formGroup.controls.field.disable();
    }

    if (this.lockSortOptions()) {
      formGroup.controls.order.disable();
      formGroup.controls.pageBreak.disable();
    }

    return formGroup;
  }

  getSortConfig(index: number): {
    canDelete: boolean;
    showPageBreak: boolean;
    updateable: boolean;
  } | null {
    if (index >= this.masterSortBreakOptions.length) {
      return { canDelete: true, showPageBreak: true, updateable: true };
    }

    const sortBreakOption = this.masterSortBreakOptions[index];
    return {
      canDelete: sortBreakOption.canDelete ?? true,
      showPageBreak: sortBreakOption.pageBreak ?? true,
      updateable: sortBreakOption.updateable ?? true,
    };
  }

  isLastFilledSort(index: number): boolean {
    let filledCount = 0;
    let lastFilledIndex = -1;
    for (let i = 0; i < this.sorts.length; i++) {
      if (this.sorts[i].controls.field.value) {
        filledCount++;
        lastFilledIndex = i;
      }
    }
    return filledCount >= 2 && index === lastFilledIndex;
  }

  removeSort(idOrIndex: string | number) {
    const i =
      typeof idOrIndex === 'number'
        ? idOrIndex
        : this.sorts.findIndex((fg) => fg.controls.id.value === idOrIndex);
    if (i < 0) return;
    const sortFg = this.sorts[i];
    const canDelete = sortFg?.controls['canDelete']?.value ?? true;
    if (!canDelete) return;
    this.fg.controls.sorts.removeAt(i);
    this.masterSortBreakOptions.splice(i, 1);
    this.sortsCount.set(this.sorts.length);
    this.updateReport();
  }

  clearSort(i: number) {
    const sortFg = this.sorts[i];
    sortFg.controls['field'].setValue(null);
    this.updateReport();
  }

  addSort() {
    if (!this.canAddMore()) return;

    const currentIndex = this.sorts.length;
    const sortBreakOption = this.masterSortBreakOptions[currentIndex];

    if (sortBreakOption && sortBreakOption.updateable === false) return;

    const placeholderOption: SortBreakOption = sortBreakOption ?? {
      breakId: currentIndex + 1,
      level: currentIndex + 1,
      updateable: true,
      canDelete: true,
      columnName: '',
      tableAlias: '',
      defaultValue: '',
      defaultSort: SORT_ASC,
      pageBreak: true,
      pageBreakChecked: false,
    };

    const formGroup = this.createFormGroup(placeholderOption, false);
    this.fg.controls.sorts.push(formGroup);
    this.sortsCount.set(this.sorts.length);
  }

  onEdit(index: number): void {
    const sortFg = this.sorts[index];
    if (!sortFg) return;
    this.showDialog(this.fieldDialog, sortFg.controls.field, sortFg);
  }

  onListChanged(e: ReorderableFormChangedEvent): void {
    if (e.reason === 'remove') {
      this.masterSortBreakOptions.splice(e.previousIndex, 1);
      this.sortsCount.set(this.sorts.length);
      this.updateReport();
      return;
    }
    this.syncSortSequences();
    this.clearPageBreakOutsideAllowedSlots();
    this.updateReport();
  }

  private syncSortSequences(): void {
    this.sorts.forEach((sortFg, i) => {
      const newSequence = i + 1;
      sortFg.controls['breakId'].setValue(newSequence);
      sortFg.controls['level'].setValue(newSequence);
    });
  }

  /**
   * Page-break checkbox is slot-based (template `pageBreak` on that index).
   * Checked state must not survive a move into a slot that cannot show it.
   */
  private clearPageBreakOutsideAllowedSlots(): void {
    this.sorts.forEach((sortFg, i) => {
      if (this.getSortConfig(i)?.showPageBreak) {
        return;
      }
      if (sortFg.controls.pageBreak.value) {
        sortFg.controls.pageBreak.setValue(false, { emitEvent: false });
      }
    });
  }

  showDialog(
    dialog: ReportFieldDialogComponent,
    control: FormControl<ReportField | null>,
    sortFg: SortFormGroup,
  ) {
    if (control.disabled) {
      return;
    }
    this.current = control;
    const report = this.reports.currentReport();
    const reportId = report?.reportTemplate?.id ?? null;
    const breakId = sortFg.controls['breakId']?.value ?? null;
    dialog.show(reportId, null, breakId);
  }

  selected(item: ReportField) {
    const isDuplicate = this.sorts.some((sortFg) => {
      const field = (sortFg.controls['field'] as FormControl)?.value;
      return (
        field &&
        sortFg.controls['field'] !== this.current &&
        field.tableAlias === item.tableAlias &&
        field.columnName === item.columnName
      );
    });
    if (isDuplicate) {
      this.snackbar.show('Duplicate', 'Error');
      return;
    }
    this.current.setValue(item);
    this.updateReport();
  }

  updateReport() {
    this.reports.currentReport.update((report) => {
      if (!report?.reportRequest) {
        return report;
      }

      const sortBreakOptions = report.reportRequest.sortBreakOptions || [];

      const updatedSortBreakOptions = this.sorts
        .map((sortFg, index) => {
          const field = sortFg.controls['field'].value;
          const sortBreakOption = sortBreakOptions[index];

          const currentColumn = field?.columnName || '';
          const currentDefaultValue =
            field?.defaultValue || field?.columnName || '';
          const currentTableAlias = field?.tableAlias || '';
          const currentSortOrder = (sortFg.controls['order'].value ||
            SORT_ASC) as typeof SORT_ASC | typeof SORT_DESC;
          const slotAllowsPageBreak =
            this.getSortConfig(index)?.showPageBreak ?? false;
          const currentPageBreak =
            slotAllowsPageBreak &&
            (sortFg.controls['pageBreak'].value || false);

          return {
            breakId:
              sortFg.controls['breakId']?.value ||
              sortBreakOption?.breakId ||
              index + 1,
            level:
              sortFg.controls['level']?.value ||
              sortBreakOption?.level ||
              index + 1,
            updateable:
              sortFg.controls['updateable']?.value ??
              sortBreakOption?.updateable ??
              true,
            canDelete:
              sortFg.controls['canDelete']?.value ??
              sortBreakOption?.canDelete ??
              true,
            columnName: currentColumn,
            tableAlias: currentTableAlias,
            defaultValue: currentDefaultValue,
            defaultSort: currentSortOrder,
            pageBreak: sortBreakOption?.pageBreak ?? true,
            pageBreakChecked: currentPageBreak,
          };
        })
        .filter((sbo) => sbo.columnName);

      // Return a new object reference so downstream signals detect the change
      return {
        ...report,
        reportRequest: {
          ...report.reportRequest,
          sortBreakOptions: updatedSortBreakOptions,
        },
      };
    });
  }
}
